import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { AppContext } from '../../context/AppContext';
import { Reclamacion, Socio, Carrera, Liga } from '../../models/types';
import { FaExclamationTriangle, FaCheck, FaTimes, FaEye, FaPlus, FaFilter, FaGavel, FaTrash } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { reclamacionServiceFirestore } from '../../services/ReclamacionServiceFirestore';
import { toast } from 'react-toastify';
import { theme } from '../../theme';
import { SectionHeaderComp } from '../ligas/LastresLigaPage';

// Responsive breakpoints
const breakpoints = {
  mobile: '768px',
  tablet: '1024px'
};

// Estilos
const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 8px;
    max-width: 100%;
    margin: 0;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
    gap: 15px;
    margin-bottom: 20px;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 2.2rem;
  font-weight: ${theme.typography.fontWeights.bold};
  
  svg {
    color: ${theme.colors.primary.main};
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.6rem;
    justify-content: center;
  }
`;

const Button = styled.button`
  background-color: ${theme.colors.primary.main};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background-color: ${theme.colors.secondary.main};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  svg {
    margin-right: 8px;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    width: 100%;
    justify-content: center;
    padding: 12px 20px;
    font-size: 16px;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
  
  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px rgba(16, 19, 37, 0.1);
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    width: 100%;
    padding: 10px 12px;
    font-size: 16px;
  }
`;

const FilterLabel = styled.label`
  font-weight: ${theme.typography.fontWeights.medium};
  color: ${theme.colors.text.primary};
  margin-right: 8px;
  
  @media (max-width: ${breakpoints.mobile}) {
    margin-right: 0;
    margin-bottom: 5px;
    font-size: 14px;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  
  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
  
  th, td {
    padding: 15px;
    text-align: left;
    border-bottom: 1px solid #eee;
  }
  
  th {
    background-color: ${theme.colors.primary.main};
    font-weight: ${theme.typography.fontWeights.semibold};
    color: white;
  }
  
  tr:hover {
    background-color: ${theme.colors.background.default};
  }
  
  tr:last-child td {
    border-bottom: none;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    display: none;
  }
`;

// Componentes para vista móvil
const MobileContainer = styled.div`
  display: none;
  
  @media (max-width: ${breakpoints.mobile}) {
    display: block;
  }
`;

const MobileCard = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${theme.colors.primary.main};
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const MobileCardTitle = styled.div`
  font-weight: bold;
  font-size: 16px;
  color: ${theme.colors.text.primary};
  flex: 1;
  margin-right: 10px;
`;

const MobileCardInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 14px;
`;

const MobileCardInfoItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const MobileCardLabel = styled.span`
  font-weight: bold;
  color: ${theme.colors.text.secondary};
  font-size: 12px;
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const MobileCardValue = styled.span`
  color: ${theme.colors.text.primary};
`;

const MobileCardDescription = styled.div`
  margin-bottom: 12px;
  padding: 8px 12px;
  background-color: ${theme.colors.background.default};
  border-radius: 6px;
  font-size: 14px;
  color: ${theme.colors.text.secondary};
`;

const MobileCardActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const MobileActionButton = styled.button<{ variant?: 'primary' | 'success' | 'danger' | 'warning' }>`
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
  flex: 1;
  min-width: 80px;
  justify-content: center;
  
  background-color: ${props => {
    switch (props.variant) {
      case 'success': return theme.colors.success.main;
      case 'danger': return theme.colors.error.main;
      case 'warning': return theme.colors.warning.main;
      default: return theme.colors.primary.main;
    }
  }};
  
  color: white;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  svg {
    font-size: 12px;
  }
`;

const StatusBadge = styled.span<{ $status: 'pendiente' | 'aceptada' | 'rechazada' | 'sancionada' }>`
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
  background-color: ${props => {
    switch (props.$status) {
      case 'pendiente': return theme.colors.warning.main;
      case 'aceptada': return theme.colors.success.main;
      case 'rechazada': return theme.colors.secondary.main;
      case 'sancionada': return theme.colors.info.main;
      default: return theme.colors.warning.main;
    }
  }};
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
  }
`;

const ActionButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  margin-right: 10px;
  color: ${theme.colors.primary.main};
  font-size: 1.1rem;
  padding: 5px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${theme.colors.secondary.main};
    background-color: rgba(0, 0, 0, 0.05);
    transform: scale(1.1);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  color: ${theme.colors.text.secondary};
  
  svg {
    font-size: 3rem;
    color: ${theme.colors.primary.main};
    opacity: 0.7;
  }
  
  h3 {
    margin: 10px 0;
    color: ${theme.colors.text.primary};
  }
  
  p {
    margin: 0;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 30px 16px;
    gap: 12px;
    
    svg {
      font-size: 2.5rem !important;
    }
    
    h3 {
      font-size: 16px;
      margin: 8px 0;
    }
    
    p {
      font-size: 14px;
    }
  }
`;

const Tabs = styled.div`
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid #eee;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 10px 20px;
  background-color: ${props => props.$active ? theme.colors.primary.main : 'transparent'};
  color: ${props => props.$active ? 'white' : theme.colors.text.primary};
  border: none;
  cursor: pointer;
  font-weight: ${props => props.$active ? theme.typography.fontWeights.semibold : theme.typography.fontWeights.regular};
  transition: all 0.3s ease;
  border-bottom: 2px solid ${props => props.$active ? theme.colors.secondary.main : 'transparent'};
  
  &:hover {
    background-color: ${props => props.$active ? theme.colors.primary.main : theme.colors.background.default};
  }
`;

const InfoMessage = styled.div`
  background-color: rgba(16, 19, 37, 0.03);
  border-left: 4px solid ${theme.colors.primary.main};
  color: ${theme.colors.text.primary};
  padding: 16px 20px;
  margin-bottom: 25px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  line-height: 1.6;
  font-size: 0.95rem;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 12px 16px;
    margin-bottom: 20px;
    font-size: 14px;
    line-height: 1.5;
  }
`;

// Componente principal
const ReclamacionesList: React.FC = () => {
  const navigate = useNavigate();
  const { data, loadData } = useContext(AppContext);
  const [filtroEstado, setFiltroEstado] = useState<string>('todos');
  const [filtroLiga, setFiltroLiga] = useState<string>('todos');
  const [filtroCarrera, setFiltroCarrera] = useState<string>('todos');
  const [filtroPiloto, setFiltroPiloto] = useState<string>('todos');
  const [reclamacionesFiltradas, setReclamacionesFiltradas] = useState<Reclamacion[]>([]);
  const [isComisario, setIsComisario] = useState(false); // Por defecto, el usuario no es comisario
  const [isAdmin, setIsAdmin] = useState(false); // Para verificar si el usuario es admin o superadmin
  
  const { currentUser, hasPermission, hasAnyRole } = useAuth();
  
  // Verificar roles y permisos del usuario actual
  useEffect(() => {
    // Verificar si el usuario es comisario (tiene permiso para gestionar reclamaciones)
    setIsComisario(hasPermission('manage:claims'));
    
    // Verificar si el usuario es admin o superadmin
    setIsAdmin(hasAnyRole(['admin', 'superadmin']));
  }, [currentUser, hasPermission, hasAnyRole]);
  
  // Inicializar y aplicar filtros cuando cambian los datos
  useEffect(() => {
    if (!data.reclamaciones) {
      data.reclamaciones = [];
    }
    
    aplicarFiltros();
  }, [data, filtroEstado, filtroLiga, filtroCarrera, filtroPiloto]);
  
  // Aplicar filtros a las reclamaciones
  const aplicarFiltros = () => {
    let reclamacionesFiltradas = [...(data.reclamaciones || [])];
    
    // Aplicar filtro de estado
    if (filtroEstado !== 'todos') {
      reclamacionesFiltradas = reclamacionesFiltradas.filter(
        reclamacion => reclamacion.estado === filtroEstado
      );
    }
    
    // Aplicar filtro de liga
    if (filtroLiga !== 'todos') {
      reclamacionesFiltradas = reclamacionesFiltradas.filter(
        reclamacion => reclamacion.ligaId === filtroLiga
      );
    }
    
    // Aplicar filtro de carrera
    if (filtroCarrera !== 'todos') {
      reclamacionesFiltradas = reclamacionesFiltradas.filter(
        reclamacion => reclamacion.carreraId === filtroCarrera
      );
    }
    
    // Aplicar filtro de piloto (tanto reclamante como reclamado)
    if (filtroPiloto !== 'todos') {
      reclamacionesFiltradas = reclamacionesFiltradas.filter(
        reclamacion => reclamacion.reclamanteSocioId === filtroPiloto || reclamacion.reclamadoSocioId === filtroPiloto
      );
    }
    
    setReclamacionesFiltradas(reclamacionesFiltradas);
  };
  
  // Obtener nombre de piloto por ID
  const getNombrePiloto = (socioId: string) => {
    const socio = data.socios.find((s: Socio) => s.id === socioId);
    if (!socio) return 'Piloto desconocido';
    
    // Intentar usar diferentes campos según disponibilidad
    if (socio.display_name) return socio.display_name;
    if (socio.user_login) return socio.user_login;
    if (socio.NOMBRE_SERVERS_VRS) return socio.NOMBRE_SERVERS_VRS;
    if (socio.nombre && socio.apellido) return `${socio.nombre} ${socio.apellido}`;
    
    return 'Piloto sin nombre';
  };
  
  // Obtener nombre de carrera por ID
  const getNombreCarrera = (carreraId: string) => {
    const carrera = data.carreras.find((c: Carrera) => c.id === carreraId);
    return carrera ? carrera.nombre : 'Carrera desconocida';
  };
  
  // Obtener nombre de liga por ID
  const getNombreLiga = (ligaId: string) => {
    const liga = data.ligas.find((l: Liga) => l.id === ligaId);
    return liga ? liga.nombre : 'Liga desconocida';
  };
  
  // Obtener carreras filtradas por liga
  const getCarrerasPorLiga = (ligaId: string) => {
    if (ligaId === 'todos') {
      return data.carreras;
    }
    return data.carreras.filter((c: Carrera) => c.ligaId === ligaId);
  };
  
  // Función para eliminar una reclamación (solo admin/superadmin)
  const handleEliminarReclamacion = async (reclamacionId: string) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta reclamación? Esta acción no se puede deshacer.')) {
      try {
        const success = await reclamacionServiceFirestore.delete(reclamacionId);
        if (success) {
          toast.success('Reclamación eliminada correctamente');
          // Actualizar la lista de reclamaciones
          await loadData();
          // Volver a aplicar los filtros
          aplicarFiltros();
        } else {
          toast.error('Error al eliminar la reclamación');
        }
      } catch (error) {
        console.error('Error al eliminar la reclamación:', error);
        toast.error('Error al eliminar la reclamación');
      }
    }
  };

  return (
    <Container>
      <SectionHeaderComp icon={<FaGavel style={{ color: '#e4312e' }} />}>
        Sistema de Reclamaciones
      </SectionHeaderComp>
      
      <Header>
        <Button onClick={() => navigate('/reclamaciones/new')}>
          <FaPlus /> Nueva Reclamación
        </Button>
      </Header>
      
      <InfoMessage>
        Bienvenido al sistema de reclamaciones. Aquí los pilotos pueden presentar reclamaciones sobre incidentes ocurridos durante las carreras. Los comisarios revisarán cada caso y tomarán las decisiones correspondientes.
      </InfoMessage>
      
      <FilterContainer>
        <FilterGroup>
          <FilterLabel>Estado:</FilterLabel>
          <FilterSelect 
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
          >
            <option value="todos">Todos</option>
            <option value="pendiente">Pendientes</option>
            <option value="aceptada">Aceptadas</option>
            <option value="rechazada">Rechazadas</option>
            <option value="sancionada">Sancionadas</option>
          </FilterSelect>
        </FilterGroup>
        
        <FilterGroup>
          <FilterLabel>Liga:</FilterLabel>
          <FilterSelect 
            value={filtroLiga}
            onChange={(e) => {
              setFiltroLiga(e.target.value);
              setFiltroCarrera('todos'); // Resetear el filtro de carrera al cambiar de liga
            }}
          >
            <option value="todos">Todas</option>
            {data.ligas.map((liga: Liga) => (
              <option key={liga.id} value={liga.id}>
                {liga.nombre}
              </option>
            ))}
          </FilterSelect>
        </FilterGroup>
        
        <FilterGroup>
          <FilterLabel>Carrera:</FilterLabel>
          <FilterSelect 
            value={filtroCarrera}
            onChange={(e) => setFiltroCarrera(e.target.value)}
            disabled={filtroLiga === 'todos'}
          >
            <option value="todos">Todas</option>
            {getCarrerasPorLiga(filtroLiga).map((carrera: Carrera) => (
              <option key={carrera.id} value={carrera.id}>
                {carrera.nombre}
              </option>
            ))}
          </FilterSelect>
        </FilterGroup>
        
        <FilterGroup>
          <FilterLabel>Piloto:</FilterLabel>
          <FilterSelect 
            value={filtroPiloto}
            onChange={(e) => setFiltroPiloto(e.target.value)}
          >
            <option value="todos">Todos</option>
            {data.socios.map((socio: Socio) => (
              <option key={socio.id} value={socio.id}>
                {socio.nombre} {socio.apellido}
              </option>
            ))}
          </FilterSelect>
        </FilterGroup>
      </FilterContainer>
      
      {reclamacionesFiltradas.length > 0 ? (
        <>
          {/* Vista Desktop - Tabla */}
          <Table>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Liga</th>
                <th>Carrera</th>
                <th>Reclamante</th>
                <th>Reclamado</th>
                <th>Motivo</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {reclamacionesFiltradas.map(reclamacion => (
                <tr key={reclamacion.id}>
                  <td>{reclamacion.fechaReclamacion}</td>
                  <td>{getNombreLiga(reclamacion.ligaId)}</td>
                  <td>{getNombreCarrera(reclamacion.carreraId)}</td>
                  <td>{getNombrePiloto(reclamacion.reclamanteSocioId)}</td>
                  <td>{getNombrePiloto(reclamacion.reclamadoSocioId)}</td>
                  <td>{reclamacion.motivo}</td>
                  <td>
                    <StatusBadge $status={reclamacion.estado as any}>
                      {reclamacion.estado === 'pendiente' && 'Pendiente'}
                      {reclamacion.estado === 'aceptada' && 'Aceptada'}
                      {reclamacion.estado === 'rechazada' && 'Rechazada'}
                      {reclamacion.estado === 'sancionada' && 'Sancionada'}
                    </StatusBadge>
                  </td>
                  <td>
                    <ActionButton onClick={() => navigate(`/reclamaciones/${reclamacion.id}`)}>
                      <FaEye size={18} title="Ver detalles" />
                    </ActionButton>
                    {reclamacion.estado === 'pendiente' && isComisario && (
                      <>
                        <ActionButton onClick={() => navigate(`/reclamaciones/${reclamacion.id}/resolver?accion=aceptar`)}>
                          <FaCheck size={18} title="Aceptar reclamación" />
                        </ActionButton>
                        <ActionButton onClick={() => navigate(`/reclamaciones/${reclamacion.id}/resolver?accion=rechazar`)}>
                          <FaTimes size={18} title="Rechazar reclamación" />
                        </ActionButton>
                        <ActionButton onClick={() => navigate(`/sanciones/new?reclamacion=${reclamacion.id}`)}>
                          <FaGavel size={18} title="Aplicar sanción" />
                        </ActionButton>
                      </>
                    )}
                    {isAdmin && (
                      <ActionButton onClick={() => handleEliminarReclamacion(reclamacion.id)} style={{ color: theme.colors.error.main }}>
                        <FaTrash size={16} title="Eliminar reclamación" />
                      </ActionButton>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Vista Móvil - Cards */}
          <MobileContainer>
            {reclamacionesFiltradas.map(reclamacion => (
              <MobileCard key={reclamacion.id}>
                <MobileCardHeader>
                  <MobileCardTitle>
                    {getNombreCarrera(reclamacion.carreraId)}
                  </MobileCardTitle>
                  <StatusBadge $status={reclamacion.estado as any}>
                    {reclamacion.estado === 'pendiente' && 'Pendiente'}
                    {reclamacion.estado === 'aceptada' && 'Aceptada'}
                    {reclamacion.estado === 'rechazada' && 'Rechazada'}
                    {reclamacion.estado === 'sancionada' && 'Sancionada'}
                  </StatusBadge>
                </MobileCardHeader>

                <MobileCardInfo>
                  <MobileCardInfoItem>
                    <MobileCardLabel>Liga</MobileCardLabel>
                    <MobileCardValue>{getNombreLiga(reclamacion.ligaId)}</MobileCardValue>
                  </MobileCardInfoItem>
                  <MobileCardInfoItem>
                    <MobileCardLabel>Fecha</MobileCardLabel>
                    <MobileCardValue>{reclamacion.fechaReclamacion}</MobileCardValue>
                  </MobileCardInfoItem>
                  <MobileCardInfoItem>
                    <MobileCardLabel>Reclamante</MobileCardLabel>
                    <MobileCardValue>{getNombrePiloto(reclamacion.reclamanteSocioId)}</MobileCardValue>
                  </MobileCardInfoItem>
                  <MobileCardInfoItem>
                    <MobileCardLabel>Reclamado</MobileCardLabel>
                    <MobileCardValue>{getNombrePiloto(reclamacion.reclamadoSocioId)}</MobileCardValue>
                  </MobileCardInfoItem>
                </MobileCardInfo>

                <MobileCardDescription>
                  <strong>Motivo:</strong> {reclamacion.motivo}
                </MobileCardDescription>

                <MobileCardActions>
                  <MobileActionButton 
                    onClick={() => navigate(`/reclamaciones/${reclamacion.id}`)}
                  >
                    <FaEye /> Ver
                  </MobileActionButton>
                  
                  {reclamacion.estado === 'pendiente' && isComisario && (
                    <>
                      <MobileActionButton 
                        variant="success"
                        onClick={() => navigate(`/reclamaciones/${reclamacion.id}/resolver?accion=aceptar`)}
                      >
                        <FaCheck /> Aceptar
                      </MobileActionButton>
                      <MobileActionButton 
                        variant="danger"
                        onClick={() => navigate(`/reclamaciones/${reclamacion.id}/resolver?accion=rechazar`)}
                      >
                        <FaTimes /> Rechazar
                      </MobileActionButton>
                      <MobileActionButton 
                        variant="warning"
                        onClick={() => navigate(`/sanciones/new?reclamacion=${reclamacion.id}`)}
                      >
                        <FaGavel /> Sancionar
                      </MobileActionButton>
                    </>
                  )}
                  
                  {isAdmin && (
                    <MobileActionButton 
                      variant="danger"
                      onClick={() => handleEliminarReclamacion(reclamacion.id)}
                    >
                      <FaTrash /> Eliminar
                    </MobileActionButton>
                  )}
                </MobileCardActions>
              </MobileCard>
            ))}
          </MobileContainer>
        </>
      ) : (
        <EmptyState>
          <FaExclamationTriangle size={40} />
          <h3>No hay reclamaciones que coincidan con los filtros seleccionados</h3>
          <p>Intenta cambiar los filtros o crea una nueva reclamación</p>
        </EmptyState>
      )}
    </Container>
  );
};

export default ReclamacionesList;
